import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lisStagesApi, type LisStage, type LisStageTestSetup } from '@/api/lisStagesApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Stage configuration and availability.
 *
 * Every key carries `selectedTenantId` — these decide what a lab tech may send
 * to a laboratory, so another tenant's stages on screen would be worse than
 * none at all.
 */
export function useLisStages() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-stages', tenantId],
    enabled: !!tenantId,
    queryFn: () => lisStagesApi.list(),
  })
}

/**
 * Which stages can be ordered for one product right now.
 *
 * `staleTime: 0` — a result landing opens the next stage, and a cached "not
 * yet" would keep an operator locked out of work they can actually do.
 */
export function useLisStageAvailability(productId: string | null, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-stages', 'availability', tenantId, productId],
    enabled: enabled && !!tenantId && !!productId,
    queryFn: () => lisStagesApi.availability(productId as string),
    staleTime: 0,
  })
}

function useStageMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis-stages'] })
    },
  })
}

export function useCreateLisStage() {
  return useStageMutation<{ name: string; description?: string; sortOrder?: number }, LisStage>(
    (b) => lisStagesApi.create(b),
  )
}

export function useUpdateLisStage() {
  return useStageMutation<
    { id: string; name?: string; description?: string | null; sortOrder?: number; active?: boolean },
    LisStage
  >(({ id, ...body }) => lisStagesApi.update(id, body))
}

export function useRemoveLisStage() {
  return useStageMutation<string, { removed: boolean }>((id) => lisStagesApi.remove(id))
}

export function useAddStagePredecessor() {
  return useStageMutation<{ id: string; predecessorId: string }, LisStage>(({ id, predecessorId }) =>
    lisStagesApi.addPredecessor(id, predecessorId),
  )
}

export function useRemoveStagePredecessor() {
  return useStageMutation<{ id: string; predecessorId: string }, LisStage>(({ id, predecessorId }) =>
    lisStagesApi.removePredecessor(id, predecessorId),
  )
}

export function useAddStageTestCode() {
  return useStageMutation<
    LisStageTestSetup & { id: string; testCode: string; defaultSelected?: boolean },
    LisStage
  >(({ id, ...body }) => lisStagesApi.addTestCode(id, body))
}

export function useUpdateStageTestCode() {
  return useStageMutation<
    LisStageTestSetup & { testCodeId: string; defaultSelected?: boolean; active?: boolean },
    LisStage
  >(({ testCodeId, ...body }) => lisStagesApi.updateTestCode(testCodeId, body))
}

export function useRemoveStageTestCode() {
  return useStageMutation<string, LisStage>((id) => lisStagesApi.removeTestCode(id))
}

export function useCopyStageTestCodes() {
  return useStageMutation<{ toStageId: string; fromStageId: string }, { copied: number; skipped: number }>(
    ({ toStageId, fromStageId }) => lisStagesApi.copyTestCodes(toStageId, fromStageId),
  )
}
